import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Lock, X, CreditCard, Building, Banknote, Phone, ChevronDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============================== SIGNATURE PAD ==============================

const SignaturePad = ({ onSignatureChange }) => {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const scale = window.devicePixelRatio || 1;
    canvas.width = canvas.offsetWidth * scale;
    canvas.height = canvas.offsetHeight * scale;
    ctx.scale(scale, scale);
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1a1a1a';
  }, []);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    return { x, y };
  };

  const start = (e) => {
    e.preventDefault();
    setDrawing(true);
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const draw = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
    if (!hasSignature) setHasSignature(true);
  };
  const stop = () => {
    setDrawing(false);
    if (canvasRef.current && hasSignature) {
      onSignatureChange?.(canvasRef.current.toDataURL('image/png'));
    }
  };
  const clear = () => {
    const c = canvasRef.current;
    c.getContext('2d').clearRect(0, 0, c.width, c.height);
    setHasSignature(false);
    onSignatureChange?.(null);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-bold tracking-wider uppercase text-[#666]">Signature du client</span>
        {hasSignature && (
          <button type="button" onClick={clear} className="text-[11px] font-bold text-[#F05A1A] hover:underline" data-testid="clear-signature-btn">
            ✕ Effacer
          </button>
        )}
      </div>
      <div className="relative rounded-xl overflow-hidden bg-white border-2 border-dashed border-[#d4cfc7]">
        <canvas
          ref={canvasRef}
          className="w-full h-32 touch-none cursor-crosshair"
          onMouseDown={start} onMouseMove={draw} onMouseUp={stop} onMouseLeave={stop}
          onTouchStart={start} onTouchMove={draw} onTouchEnd={stop}
          data-testid="signature-canvas"
        />
        {!hasSignature && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-[11px] tracking-wider uppercase text-[#bbb]">✍ Signe ici (souris ou doigt)</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================== PAYMENT CHOICE ==============================

const PaymentChoice = ({ value, onChange, hideStripe = false }) => {
  const opts = [
    { id: 'card', icon: CreditCard, label: 'Carte bancaire', desc: 'Paiement sécurisé Stripe · immédiat', show: !hideStripe },
    { id: 'transfer', icon: Building, label: 'Virement bancaire', desc: 'RIB envoyé par email après signature · validation manuelle', show: true },
    { id: 'onsite', icon: Banknote, label: 'Paiement sur place', desc: 'CB / espèces / chèque lors du 1er rendez-vous · à valider avec Sabril', show: true },
  ].filter(o => o.show);
  return (
    <div className="grid grid-cols-1 gap-2">
      {opts.map((o) => (
        <label
          key={o.id}
          className="flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all"
          style={{
            borderColor: value === o.id ? '#F05A1A' : '#e0dbd4',
            backgroundColor: value === o.id ? 'rgba(240,90,26,0.06)' : '#fff',
          }}
          data-testid={`payment-choice-${o.id}`}
        >
          <input
            type="radio"
            name="payment_method"
            value={o.id}
            checked={value === o.id}
            onChange={() => onChange(o.id)}
            className="w-4 h-4 accent-[#F05A1A]"
          />
          <o.icon className="w-5 h-5 text-[#F05A1A]" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-[#1a1a1a] leading-tight">{o.label}</p>
            <p className="text-[11px] text-[#666] mt-0.5">{o.desc}</p>
          </div>
        </label>
      ))}
    </div>
  );
};

// ============================== MAIN MODAL ==============================
/**
 * Generic contract modal usable for any offer.
 * offer = {
 *   id, name, price (number or null for "sur devis"), engagement,
 *   description, nda (bool), conditions (array of strings), mode: 'one_shot' | 'subscription' | 'discovery'
 * }
 */
const OfferContractModal = ({ open, onClose, offer }) => {
  // Required
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  // Optional
  const [restaurant, setRestaurant] = useState('');
  const [phone, setPhone] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [siret, setSiret] = useState('');
  const [address, setAddress] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  const [brief, setBrief] = useState('');
  const [showOptional, setShowOptional] = useState(false);

  const [paymentMethod, setPaymentMethod] = useState('card');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedNDA, setAcceptedNDA] = useState(false);
  const [acceptedCommitment, setAcceptedCommitment] = useState(false);
  const [acceptedWorkframe, setAcceptedWorkframe] = useState(false);
  const [acceptedReadiness, setAcceptedReadiness] = useState(false);
  const [confirmedItems, setConfirmedItems] = useState(new Set());
  const [signature, setSignature] = useState(null);
  const [loading, setLoading] = useState(false);

  // ============================== LocalStorage persistence ==============================
  const storageKey = `prsnl-contract-draft-${offer?.id || 'default'}`;
  const [wasRestored, setWasRestored] = useState(false);
  const [hasLoadedDraft, setHasLoadedDraft] = useState(false);

  // Reset load flag when modal closes — allows re-loading the draft on next open
  useEffect(() => {
    if (!open) {
      setHasLoadedDraft(false);
      setWasRestored(false);
    }
  }, [open]);

  // Load draft on first mount when modal opens for this offer
  useEffect(() => {
    if (!open || !offer || hasLoadedDraft) return;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const d = JSON.parse(raw);
        if (d.firstName) setFirstName(d.firstName);
        if (d.lastName) setLastName(d.lastName);
        if (d.email) setEmail(d.email);
        if (d.restaurant) setRestaurant(d.restaurant);
        if (d.phone) setPhone(d.phone);
        if (d.companyName) setCompanyName(d.companyName);
        if (d.siret) setSiret(d.siret);
        if (d.address) setAddress(d.address);
        if (d.postalCode) setPostalCode(d.postalCode);
        if (d.city) setCity(d.city);
        if (d.vatNumber) setVatNumber(d.vatNumber);
        if (d.brief) setBrief(d.brief);
        if (d.paymentMethod) setPaymentMethod(d.paymentMethod);
        if (typeof d.acceptedTerms === 'boolean') setAcceptedTerms(d.acceptedTerms);
        if (typeof d.acceptedNDA === 'boolean') setAcceptedNDA(d.acceptedNDA);
        if (typeof d.acceptedCommitment === 'boolean') setAcceptedCommitment(d.acceptedCommitment);
        if (typeof d.acceptedWorkframe === 'boolean') setAcceptedWorkframe(d.acceptedWorkframe);
        if (typeof d.acceptedReadiness === 'boolean') setAcceptedReadiness(d.acceptedReadiness);
        if (Array.isArray(d.confirmedItems)) setConfirmedItems(new Set(d.confirmedItems));
        if (typeof d.showOptional === 'boolean') setShowOptional(d.showOptional);
        const hasMeaningfulData = !!(d.firstName || d.lastName || d.email || d.acceptedTerms || (d.confirmedItems || []).length > 0);
        if (hasMeaningfulData) setWasRestored(true);
      }
    } catch (e) { /* corrupted localStorage — ignore */ }
    setHasLoadedDraft(true);
  }, [open, offer, storageKey, hasLoadedDraft]);

  // Auto-save draft on any change
  useEffect(() => {
    if (!open || !offer || !hasLoadedDraft) return;
    try {
      const draft = {
        firstName, lastName, email, restaurant, phone,
        companyName, siret, address, postalCode, city, vatNumber, brief,
        paymentMethod,
        acceptedTerms, acceptedNDA, acceptedCommitment, acceptedWorkframe, acceptedReadiness,
        confirmedItems: Array.from(confirmedItems),
        showOptional,
        savedAt: Date.now(),
      };
      const hasAny = firstName || lastName || email || acceptedTerms || acceptedNDA || confirmedItems.size > 0;
      if (hasAny) localStorage.setItem(storageKey, JSON.stringify(draft));
    } catch (e) { /* ignore */ }
  }, [
    open, offer, hasLoadedDraft, storageKey,
    firstName, lastName, email, restaurant, phone,
    companyName, siret, address, postalCode, city, vatNumber, brief,
    paymentMethod,
    acceptedTerms, acceptedNDA, acceptedCommitment, acceptedWorkframe, acceptedReadiness,
    confirmedItems, showOptional,
  ]);

  if (!open || !offer) return null;

  const isDiscovery = offer.mode === 'discovery';
  const needsSignature = !isDiscovery;
  // All scope items (included + activated options) must be confirmed one by one
  const allScopeKeys = [
    ...(offer.scopeIncluded || []).map((_, i) => `inc-${i}`),
    ...(offer.scopeOptionsActivated || []).map((_, i) => `opt-${i}`),
  ];
  const allItemsConfirmed = !needsSignature || allScopeKeys.every((k) => confirmedItems.has(k));
  const toggleItem = (key) => {
    setConfirmedItems((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const confirmAllItems = () => setConfirmedItems(new Set(allScopeKeys));

  const clearDraft = () => {
    try { localStorage.removeItem(storageKey); } catch (e) { /* ignore */ }
    setFirstName(''); setLastName(''); setEmail('');
    setRestaurant(''); setPhone('');
    setCompanyName(''); setSiret(''); setAddress(''); setPostalCode(''); setCity(''); setVatNumber('');
    setBrief(''); setShowOptional(false);
    setPaymentMethod('card');
    setAcceptedTerms(false); setAcceptedNDA(false); setAcceptedCommitment(false);
    setAcceptedWorkframe(false); setAcceptedReadiness(false);
    setConfirmedItems(new Set());
    setSignature(null);
    setWasRestored(false);
  };

  // Only firstName, lastName, email are mandatory
  const canSubmit =
    firstName && lastName && email &&
    (!needsSignature || (acceptedTerms && acceptedNDA && acceptedCommitment && acceptedWorkframe && acceptedReadiness && signature && allItemsConfirmed)) &&
    (isDiscovery || paymentMethod);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/coaching/offer/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          offer_id: offer.id,
          firstName, lastName, email,
          restaurant: restaurant || '',
          phone: phone || '',
          brief: brief || '',
          companyDetails: {
            company_name: companyName || '',
            siret: siret || '',
            address: address || '',
            postal_code: postalCode || '',
            city: city || '',
            vat_number: vatNumber || '',
          },
          scopeConfirmedCount: confirmedItems.size,
          scopeTotalCount: allScopeKeys.length,
          signature: signature || '',
          acceptedTerms: !!acceptedTerms,
          acceptedNDA: !!acceptedNDA,
          acceptedCommitment: !!acceptedCommitment,
          acceptedWorkframe: !!acceptedWorkframe,
          acceptedReadiness: !!acceptedReadiness,
          paymentMethod: isDiscovery ? null : paymentMethod,
          addOptions: offer.addOptions || {},
          origin_url: window.location.origin,
        }),
      });
      const data = await res.json();
      if (data?.checkout_url) {
        try { localStorage.removeItem(storageKey); } catch (e) { /* ignore */ }
        window.location.href = data.checkout_url;
        return;
      }
      if (data?.ok) {
        try { localStorage.removeItem(storageKey); } catch (e) { /* ignore */ }
        const msg = isDiscovery
          ? "Demande envoyée ! Sabril te recontacte sous 24h pour un appel de qualification."
          : paymentMethod === 'transfer'
            ? "Contrat signé ✅ Le contrat + RIB t'ont été envoyés par email. À toi de faire le virement."
            : paymentMethod === 'onsite'
              ? "Contrat signé ✅ On encaisse sur place lors du 1er rendez-vous. Sabril te recontacte sous 24h pour caler le démarrage."
              : "Demande envoyée. Sabril te recontacte sous 24h.";
        alert(msg);
        onClose();
        return;
      }
      alert("Erreur. Réessaie ou contacte Sabril sur Instagram @sabril.j");
    } catch (e) {
      alert("Erreur réseau. Réessaie.");
    } finally {
      setLoading(false);
    }
  };

  const today = new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  const priceLabel = offer.price ? `${offer.price.toLocaleString('fr-FR')}€ TTC` : 'Sur devis';

  // ============================== Progression tracker ==============================
  // Discovery mode = 2 steps only (identité + brief sent), paid mode = 9 steps
  const steps = isDiscovery
    ? [
        { key: 'identity', label: 'Identité', done: !!(firstName && lastName && email) },
      ]
    : [
        { key: 'identity', label: 'Identité', done: !!(firstName && lastName && email) },
        { key: 'scope', label: 'Périmètre validé', done: allItemsConfirmed && allScopeKeys.length > 0 },
        { key: 'terms', label: 'Conditions', done: acceptedTerms },
        { key: 'nda', label: 'Confidentialité', done: acceptedNDA },
        { key: 'commitment', label: 'Engagement', done: acceptedCommitment },
        { key: 'workframe', label: 'Cadre travail', done: acceptedWorkframe },
        { key: 'readiness', label: 'Investissement sur toi', done: acceptedReadiness },
        { key: 'signature', label: 'Signature', done: !!signature },
        { key: 'payment', label: 'Paiement', done: !!paymentMethod },
      ];
  const doneCount = steps.filter((s) => s.done).length;
  const totalCount = steps.length;
  const progressPct = Math.round((doneCount / totalCount) * 100);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] flex items-center justify-center p-3 sm:p-6"
        style={{ backgroundColor: 'rgba(26,26,26,0.85)', backdropFilter: 'blur(8px)' }}
        onClick={onClose}
        data-testid="offer-contract-modal"
      >
        <motion.div
          initial={{ scale: 0.94, y: 20, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.94, y: 20, opacity: 0 }}
          transition={{ type: 'spring', damping: 22 }}
          onClick={(e) => e.stopPropagation()}
          className="relative bg-[#f5f3f0] rounded-3xl w-full max-w-[760px] max-h-[92vh] overflow-y-auto"
        >
          <button
            type="button" onClick={onClose}
            className="absolute top-4 right-4 z-30 w-9 h-9 rounded-full bg-white border border-[#e0dbd4] flex items-center justify-center hover:bg-[#f0ece6] shadow-sm"
            data-testid="offer-modal-close"
          >
            <X className="w-4 h-4 text-[#666]" />
          </button>

          <div className="p-6 sm:p-9">
            <p className="text-[11px] font-bold tracking-[2.5px] uppercase text-[#F05A1A] mb-2">
              {isDiscovery ? 'Demande de discovery call' : 'Contrat & engagement'}
            </p>
            <h2 className="font-bebas text-3xl sm:text-4xl text-[#1a1a1a] leading-tight mb-1">
              {offer.name}
            </h2>
            <p className="text-sm text-[#666] mb-5">
              {priceLabel}{offer.engagement ? ` · ${offer.engagement}` : ''}
            </p>

            {/* Restore draft banner */}
            {wasRestored && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-5 p-4 rounded-2xl border border-[#1a8a3a]/30 bg-[#1a8a3a]/10 flex items-start gap-3 flex-wrap"
                data-testid="contract-restore-banner"
              >
                <div className="flex-shrink-0 w-9 h-9 rounded-full bg-[#1a8a3a] flex items-center justify-center text-white text-sm font-black">↻</div>
                <div className="flex-1 min-w-[220px]">
                  <p className="text-[13px] font-black text-[#1a8a3a] mb-1">On reprend où tu en étais</p>
                  <p className="text-[12px] text-[#1a1a1a] leading-snug">
                    On a retrouvé tes infos saisies précédemment. Tu peux continuer là où tu t'étais arrêté{firstName ? `, ${firstName}` : ''}. <strong>Rien n'est envoyé tant que tu n'as pas signé.</strong>
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => setWasRestored(false)}
                    className="px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-wider bg-[#1a8a3a] text-white hover:bg-[#147230]"
                    data-testid="restore-continue-btn"
                  >
                    ✓ Continuer
                  </button>
                  <button
                    type="button"
                    onClick={() => { if (confirm('Tout effacer et repartir de zéro ?')) clearDraft(); }}
                    className="px-3 py-1.5 rounded-full text-[11px] font-bold text-[#666] hover:text-[#1a1a1a] underline underline-offset-2"
                    data-testid="restore-reset-btn"
                  >
                    Tout effacer
                  </button>
                </div>
              </motion.div>
            )}

            {/* Progress bar sticky */}
            {!isDiscovery && (
              <div
                className="sticky top-0 -mx-6 sm:-mx-9 px-6 sm:px-9 py-3 mb-6 bg-[#f5f3f0]/95 backdrop-blur-sm z-20 border-b border-[#e0dbd4]"
                data-testid="contract-progress-tracker"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-black uppercase tracking-wider text-[#1a1a1a]">
                      Progression
                    </span>
                    <span className="text-[11px] font-bold tabular-nums" style={{ color: progressPct === 100 ? '#1a8a3a' : '#F05A1A' }}>
                      {doneCount} / {totalCount} étapes
                    </span>
                  </div>
                  <span className="text-[11px] font-black tabular-nums" style={{ color: progressPct === 100 ? '#1a8a3a' : '#F05A1A' }}>
                    {progressPct}%
                  </span>
                </div>
                {/* Bar */}
                <div className="w-full h-1.5 rounded-full bg-[#e0dbd4] overflow-hidden mb-2">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${progressPct}%`,
                      background: progressPct === 100
                        ? 'linear-gradient(90deg, #1a8a3a, #2ab84e)'
                        : 'linear-gradient(90deg, #F05A1A, #ff7a3a)',
                    }}
                  />
                </div>
                {/* Step pills */}
                <div className="flex flex-wrap gap-1.5">
                  {steps.map((s) => (
                    <span
                      key={s.key}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold transition-all"
                      style={{
                        background: s.done ? 'rgba(26,138,58,0.12)' : 'rgba(0,0,0,0.04)',
                        color: s.done ? '#1a8a3a' : '#999',
                        border: `1px solid ${s.done ? 'rgba(26,138,58,0.30)' : 'rgba(0,0,0,0.08)'}`,
                      }}
                    >
                      <span>{s.done ? '✓' : '○'}</span>
                      <span>{s.label}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Identité */}
            <div className="bg-white rounded-2xl p-5 sm:p-6 mb-5 border border-[#e0dbd4]">
              <p className="text-[11px] font-bold tracking-wider uppercase text-[#666] mb-4">1 · Tes informations</p>

              {/* Required */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input required type="text" placeholder="Prénom *" value={firstName} onChange={(e) => setFirstName(e.target.value)} className="px-4 py-3 rounded-xl border border-[#e0dbd4] bg-[#fafaf7] text-sm focus:outline-none focus:border-[#F05A1A]" data-testid="offer-first-name" />
                <input required type="text" placeholder="Nom *" value={lastName} onChange={(e) => setLastName(e.target.value)} className="px-4 py-3 rounded-xl border border-[#e0dbd4] bg-[#fafaf7] text-sm focus:outline-none focus:border-[#F05A1A]" data-testid="offer-last-name" />
                <input required type="email" placeholder="Email *" value={email} onChange={(e) => setEmail(e.target.value)} className="px-4 py-3 rounded-xl border border-[#e0dbd4] bg-[#fafaf7] text-sm focus:outline-none focus:border-[#F05A1A] sm:col-span-2" data-testid="offer-email" />
              </div>
              <p className="text-[10px] text-[#888] mt-2">* obligatoires — le reste est optionnel et peut être complété plus tard</p>

              {/* Optional toggle */}
              <button
                type="button"
                onClick={() => setShowOptional((v) => !v)}
                className="w-full mt-4 flex items-center justify-between px-4 py-3 rounded-xl bg-[#FAF6F0] border border-[#e0dbd4] hover:bg-[#f5f0e7] transition-all"
                data-testid="offer-toggle-optional"
              >
                <span className="text-[12px] font-bold tracking-wide uppercase text-[#1a1a1a]">
                  {showOptional ? '− Masquer les infos complémentaires' : '+ Compléter mes infos (optionnel)'}
                </span>
                <ChevronDown
                  className="w-4 h-4 text-[#666] transition-transform"
                  style={{ transform: showOptional ? 'rotate(180deg)' : 'rotate(0deg)' }}
                />
              </button>

              <AnimatePresence initial={false}>
                {showOptional && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="overflow-hidden"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                      <input type="text" placeholder={isDiscovery ? "Nom du groupe / enseigne" : "Nom du restaurant"} value={restaurant} onChange={(e) => setRestaurant(e.target.value)} className="px-4 py-3 rounded-xl border border-[#e0dbd4] bg-[#fafaf7] text-sm focus:outline-none focus:border-[#F05A1A] sm:col-span-2" data-testid="offer-restaurant" />
                      <input type="tel" placeholder="WhatsApp / Téléphone" value={phone} onChange={(e) => setPhone(e.target.value)} className="px-4 py-3 rounded-xl border border-[#e0dbd4] bg-[#fafaf7] text-sm focus:outline-none focus:border-[#F05A1A]" data-testid="offer-phone" />
                      <input type="text" placeholder="Raison sociale (SARL, SAS…)" value={companyName} onChange={(e) => setCompanyName(e.target.value)} className="px-4 py-3 rounded-xl border border-[#e0dbd4] bg-[#fafaf7] text-sm focus:outline-none focus:border-[#F05A1A]" data-testid="offer-company-name" />
                      <input type="text" placeholder="SIRET (14 chiffres)" value={siret} onChange={(e) => setSiret(e.target.value.replace(/\D/g, '').slice(0, 14))} maxLength={14} className="px-4 py-3 rounded-xl border border-[#e0dbd4] bg-[#fafaf7] text-sm focus:outline-none focus:border-[#F05A1A]" data-testid="offer-siret" />
                      <input type="text" placeholder="N° TVA intracom (optionnel)" value={vatNumber} onChange={(e) => setVatNumber(e.target.value)} className="px-4 py-3 rounded-xl border border-[#e0dbd4] bg-[#fafaf7] text-sm focus:outline-none focus:border-[#F05A1A] sm:col-span-2" data-testid="offer-vat" />
                      <input type="text" placeholder="Adresse du restaurant" value={address} onChange={(e) => setAddress(e.target.value)} className="px-4 py-3 rounded-xl border border-[#e0dbd4] bg-[#fafaf7] text-sm focus:outline-none focus:border-[#F05A1A] sm:col-span-2" data-testid="offer-address" />
                      <input type="text" placeholder="Code postal" value={postalCode} onChange={(e) => setPostalCode(e.target.value.replace(/\D/g, '').slice(0, 5))} maxLength={5} className="px-4 py-3 rounded-xl border border-[#e0dbd4] bg-[#fafaf7] text-sm focus:outline-none focus:border-[#F05A1A]" data-testid="offer-postal-code" />
                      <input type="text" placeholder="Ville" value={city} onChange={(e) => setCity(e.target.value)} className="px-4 py-3 rounded-xl border border-[#e0dbd4] bg-[#fafaf7] text-sm focus:outline-none focus:border-[#F05A1A]" data-testid="offer-city" />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <textarea
                placeholder={isDiscovery ? "Brief rapide : nombre de restos, pays, projet en cours…" : "Quelques mots sur ton projet (optionnel)"}
                value={brief}
                onChange={(e) => setBrief(e.target.value)}
                maxLength={400}
                rows={3}
                className="w-full mt-3 px-4 py-3 rounded-xl border border-[#e0dbd4] bg-[#fafaf7] text-sm focus:outline-none focus:border-[#F05A1A] resize-none"
                data-testid="offer-brief"
              />
            </div>

            {needsSignature && (
              <>
                {/* Conditions */}
                <div className="bg-white rounded-2xl p-5 sm:p-6 mb-5 border border-[#e0dbd4]">
                  <p className="text-[11px] font-bold tracking-wider uppercase text-[#666] mb-4">2 · Conditions essentielles</p>
                  <ul className="space-y-2 text-sm text-[#1a1a1a] leading-relaxed">
                    <li>• <strong>Tarif :</strong> {priceLabel}{offer.payment_terms ? `, ${offer.payment_terms}` : ''}.</li>
                    {offer.engagement && <li>• <strong>Engagement :</strong> {offer.engagement}.</li>}
                    {(offer.conditions || []).map((c, i) => <li key={i}>• {c}</li>)}
                  </ul>
                </div>

                {/* Périmètre détaillé : ce qu'on fait / ce qu'on ne fait pas */}
                {(offer.scopeIncluded?.length > 0 || offer.scopeExcluded) && (
                  <div className="bg-white rounded-2xl p-5 sm:p-6 mb-5 border border-[#e0dbd4]" data-testid="contract-scope-section">
                    <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
                      <p className="text-[11px] font-bold tracking-wider uppercase text-[#666]">3 · Périmètre de l'accompagnement</p>
                      {allScopeKeys.length > 0 && (
                        <div className="flex items-center gap-2 text-[11px]">
                          <span className="font-bold tabular-nums" style={{ color: allItemsConfirmed ? '#1a8a3a' : '#F05A1A' }}>
                            {confirmedItems.size} / {allScopeKeys.length} validés
                          </span>
                          {!allItemsConfirmed && (
                            <button
                              type="button"
                              onClick={confirmAllItems}
                              className="px-3 py-1 rounded-full bg-[#1a1a1a] text-white text-[10px] font-black uppercase tracking-wider hover:bg-[#000]"
                              data-testid="confirm-all-items-btn"
                            >
                              ✓ Tout valider
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    <p className="text-[12px] text-[#666] mb-4 italic">
                      Coche chaque point pour confirmer que tu as bien compris. Ça scelle ton consentement sur le périmètre exact.
                    </p>

                    {/* Ce qu'on fait */}
                    {offer.scopeIncluded?.length > 0 && (
                      <div className="mb-5">
                        <p className="text-[11px] font-black tracking-wider uppercase text-[#1a8a3a] mb-3 flex items-center gap-2">
                          <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#1a8a3a] text-white text-[10px] leading-none">✓</span>
                          Ce qu'on fait — inclus dans le forfait
                        </p>
                        <ul className="space-y-2">
                          {offer.scopeIncluded.map((item, i) => {
                            const key = `inc-${i}`;
                            const isConfirmed = confirmedItems.has(key);
                            return (
                              <li
                                key={key}
                                onClick={() => toggleItem(key)}
                                className="flex items-start gap-3 p-2.5 rounded-lg cursor-pointer transition-all"
                                style={{
                                  background: isConfirmed ? 'rgba(26,138,58,0.08)' : 'transparent',
                                  border: `1px solid ${isConfirmed ? 'rgba(26,138,58,0.30)' : 'transparent'}`,
                                }}
                                data-testid={`scope-included-${i}`}
                              >
                                <span
                                  className="inline-flex items-center justify-center flex-shrink-0 mt-0.5 w-5 h-5 rounded-md text-[11px] font-black transition-all"
                                  style={{
                                    background: isConfirmed ? '#1a8a3a' : '#fff',
                                    border: `2px solid ${isConfirmed ? '#1a8a3a' : '#d4cfc7'}`,
                                    color: isConfirmed ? '#fff' : 'transparent',
                                  }}
                                >
                                  ✓
                                </span>
                                <span className="flex-1 text-sm leading-snug">
                                  <strong className="text-[#1a1a1a]">{item.title}</strong>
                                  {item.detail && <span className="block text-[12px] text-[#666] mt-0.5">{item.detail}</span>}
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}

                    {/* Options activées en plus */}
                    {offer.scopeOptionsActivated?.length > 0 && (
                      <div className="mb-5 pt-5 border-t border-[#f0ece6]">
                        <p className="text-[11px] font-black tracking-wider uppercase text-[#F05A1A] mb-3">
                          + Options que tu as activées (facturées en plus)
                        </p>
                        <ul className="space-y-2">
                          {offer.scopeOptionsActivated.map((item, i) => {
                            const key = `opt-${i}`;
                            const isConfirmed = confirmedItems.has(key);
                            return (
                              <li
                                key={key}
                                onClick={() => toggleItem(key)}
                                className="flex items-start gap-3 p-2.5 rounded-lg cursor-pointer transition-all"
                                style={{
                                  background: isConfirmed ? 'rgba(240,90,26,0.08)' : 'transparent',
                                  border: `1px solid ${isConfirmed ? 'rgba(240,90,26,0.30)' : 'transparent'}`,
                                }}
                                data-testid={`scope-option-${i}`}
                              >
                                <span
                                  className="inline-flex items-center justify-center flex-shrink-0 mt-0.5 w-5 h-5 rounded-md text-[11px] font-black transition-all"
                                  style={{
                                    background: isConfirmed ? '#F05A1A' : '#fff',
                                    border: `2px solid ${isConfirmed ? '#F05A1A' : '#d4cfc7'}`,
                                    color: isConfirmed ? '#fff' : 'transparent',
                                  }}
                                >
                                  ✓
                                </span>
                                <span className="flex-1 text-sm leading-snug">
                                  <strong className="text-[#1a1a1a]">{item.title}</strong>
                                  {item.detail && <span className="block text-[12px] text-[#666] mt-0.5">{item.detail}</span>}
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}

                    {/* Ce qu'on ne fait pas */}
                    {offer.scopeExcluded && (
                      <div className="pt-5 border-t border-[#f0ece6]">
                        <p className="text-[11px] font-black tracking-wider uppercase text-[#a8a09a] mb-2 flex items-center gap-2">
                          <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#a8a09a] text-white text-[10px] leading-none">✕</span>
                          Ce qu'on ne fait pas — hors périmètre
                        </p>
                        <p className="text-[13px] text-[#666] leading-relaxed italic">
                          {offer.scopeExcluded}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* NDA */}
                <div className="bg-[#fef3eb] rounded-2xl p-5 sm:p-6 mb-5 border border-[#F05A1A]/25">
                  <p className="text-[11px] font-bold tracking-wider uppercase text-[#F05A1A] mb-3 flex items-center gap-2">
                    <Lock className="w-3.5 h-3.5" />
                    4 · Clause de confidentialité bilatérale
                  </p>
                  <p className="text-sm text-[#1a1a1a] leading-relaxed mb-3">
                    <strong>Sabril Jurczeszyn s'engage</strong> à conserver une stricte confidentialité sur toutes les informations stratégiques, financières et opérationnelles communiquées par le Client — aucune donnée ne sera divulguée à un tiers ni utilisée à d'autres fins que l'accompagnement.
                  </p>
                  <p className="text-sm text-[#1a1a1a] leading-relaxed">
                    <strong>Le Client s'engage</strong> de manière réciproque à conserver confidentielles les méthodes, outils, scripts et frameworks transmis par Sabril Jurczeszyn pendant et après la mission, et à ne pas les reproduire, commercialiser ou enseigner à des tiers sans autorisation écrite préalable.
                  </p>
                </div>

                {/* Engagement & Implication du Dirigeant */}
                <div className="bg-[#1a1a1a] rounded-2xl p-5 sm:p-6 mb-5 border border-[#F05A1A]/40" data-testid="contract-commitment-section">
                  <p className="text-[11px] font-bold tracking-wider uppercase text-[#F05A1A] mb-3 flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#F05A1A] text-white text-[10px] leading-none">!</span>
                    5 · Engagement & implication du dirigeant
                  </p>
                  <p className="text-sm text-white leading-relaxed mb-3">
                    Cet accompagnement n'est pas du <em className="text-[#F05A1A] not-italic font-bold">"fait à ta place"</em>. C'est une <strong className="text-[#F05A1A]">co-construction</strong> qui repose sur ton implication directe en tant que dirigeant.
                  </p>
                  <p className="text-sm text-white/85 leading-relaxed mb-4">
                    Concrètement, je vais te donner des <strong className="text-white">missions à faire entre nos sessions</strong> — des exercices structurants que tu travailles d'abord seul, puis qu'on retravaille ensemble avec mon expertise pour les amener au niveau pro. Quelques exemples&nbsp;:
                  </p>
                  <ul className="space-y-2 mb-4">
                    {[
                      { ti: "Écrire les émotions que ta marque doit susciter", de: "Pourquoi ce nom, ce concept, ce client cible — qu'est-ce que tu veux qu'on ressente en entrant ?" },
                      { ti: "Construire la journée type de ton commis", de: "Minute par minute : ouverture, mise en place, service, fermeture — pour qu'on l'optimise ensemble" },
                      { ti: "Écrire le déroulé de service de ton serveur", de: "Accueil, prise de commande, upsell, encaissement, debrief — pour qu'on cale les automatismes" },
                      { ti: "Lister tes 10 plus gros points de friction actuels", de: "Pour qu'on les attaque dans le bon ordre, pas par feeling" },
                    ].map((ex, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-[13px] leading-snug text-white/85">
                        <span className="inline-block flex-shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full bg-[#F05A1A]"></span>
                        <span>
                          <strong className="text-white">{ex.ti}</strong>
                          <span className="block text-[12px] text-white/65 mt-0.5">{ex.de}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-[12px] text-white/75 leading-relaxed italic">
                    Pourquoi ? Pour <strong className="text-white not-italic">la compréhension profonde de ton besoin</strong> (impossible si tu délègues à 100%) et pour <strong className="text-white not-italic">ta montée en compétence de dirigeant</strong> — l'objectif final, c'est que tu deviennes <strong className="text-[#F05A1A] not-italic">autonome</strong> sur les fondamentaux. Sans ton implication, l'accompagnement ne peut pas donner ses pleins résultats.
                  </p>
                </div>

                {/* Cadre de travail & communication */}
                <div className="bg-[#0f0f0f] rounded-2xl p-5 sm:p-6 mb-5 border border-[#F05A1A]/40" data-testid="contract-workframe-section">
                  <p className="text-[11px] font-bold tracking-wider uppercase text-[#F05A1A] mb-3 flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#F05A1A] text-white text-[10px] leading-none">⏱</span>
                    6 · Cadre de travail & communication
                  </p>
                  <p className="text-sm text-white leading-relaxed mb-4">
                    Pour que la mission soit <strong className="text-[#F05A1A]">productive</strong> et que je puisse te livrer un travail de qualité, on pose un cadre clair de communication. Pas pour t'éloigner, mais pour <strong className="text-white">protéger ton avancement</strong> et le mien.
                  </p>

                  {/* What we DON'T do */}
                  <div className="mb-4">
                    <p className="text-[11px] font-black tracking-wider uppercase text-[#ff6b6b] mb-2.5">❌ Ce qu'on ne fait pas</p>
                    <ul className="space-y-1.5 text-[13px] text-white/85 leading-snug">
                      <li className="flex items-start gap-2"><span className="text-[#ff6b6b] flex-shrink-0 mt-0.5">×</span><span><strong className="text-white">Pas de harcèlement WhatsApp</strong> — pas de pings toutes les heures pour relancer</span></li>
                      <li className="flex items-start gap-2"><span className="text-[#ff6b6b] flex-shrink-0 mt-0.5">×</span><span><strong className="text-white">Pas d'appels téléphoniques non planifiés</strong> — ça casse mon focus et le tien</span></li>
                      <li className="flex items-start gap-2"><span className="text-[#ff6b6b] flex-shrink-0 mt-0.5">×</span><span><strong className="text-white">Pas de messages audio</strong> — illisibles, non sourçables, je ne les ouvrirai pas</span></li>
                      <li className="flex items-start gap-2"><span className="text-[#ff6b6b] flex-shrink-0 mt-0.5">×</span><span><strong className="text-white">Les sessions de travail ne sont pas faites pour parler</strong> — elles sont faites pour <em>avancer</em> et être productifs</span></li>
                    </ul>
                  </div>

                  {/* What we DO */}
                  <div>
                    <p className="text-[11px] font-black tracking-wider uppercase text-[#7dd87d] mb-2.5">✅ Comment on bosse</p>
                    <ul className="space-y-1.5 text-[13px] text-white/85 leading-snug">
                      <li className="flex items-start gap-2"><span className="text-[#7dd87d] flex-shrink-0 mt-0.5">✓</span><span><strong className="text-white">Toute communication se fait par écrit</strong> (WhatsApp ou outil de suivi) — pour qu'on garde une trace claire</span></li>
                      <li className="flex items-start gap-2"><span className="text-[#7dd87d] flex-shrink-0 mt-0.5">✓</span><span><strong className="text-white">C'est moi qui planifie les sessions</strong> en fonction des besoins de la mission — pas de réunion improvisée</span></li>
                      <li className="flex items-start gap-2"><span className="text-[#7dd87d] flex-shrink-0 mt-0.5">✓</span><span><strong className="text-white">Tu peux laisser des commentaires async</strong> sur tout ce qu'on produit ensemble — je les traite à mon rythme de travail</span></li>
                      <li className="flex items-start gap-2"><span className="text-[#7dd87d] flex-shrink-0 mt-0.5">✓</span><span><strong className="text-white">Si parfois on a besoin de parler librement, je l'initie</strong> — mais ça reste l'exception, pas la règle</span></li>
                    </ul>
                  </div>

                  <p className="text-[12px] text-white/70 leading-relaxed italic mt-4">
                    Ce cadre n'est pas négociable. Il protège la qualité de ton accompagnement. Sans lui, je passerais 80% de mon temps à répondre à des messages et 20% à bosser pour toi — c'est l'inverse qu'on veut.
                  </p>
                </div>

                {/* Prérequis du dirigeant — Investis sur toi */}
                <div className="bg-gradient-to-br from-[#1a1a1a] to-[#000] rounded-2xl p-5 sm:p-6 mb-5 border-2 border-[#F05A1A]" data-testid="contract-readiness-section">
                  <p className="text-[11px] font-bold tracking-wider uppercase text-[#F05A1A] mb-2 flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#F05A1A] text-white text-[10px] leading-none">★</span>
                    7 · Ton plus bel investissement, c'est toi
                  </p>
                  <p className="text-white font-bebas text-2xl sm:text-3xl leading-none mb-3">
                    Tu investis sur ta marque. <span className="text-[#F05A1A]">Et sur toi.</span>
                  </p>
                  <p className="text-sm text-white/90 leading-relaxed mb-2">
                    Cet accompagnement va bien au-delà de ton restaurant. C'est une <strong className="text-white">transformation personnelle</strong> qu'on engage ensemble. Les compétences que tu vas acquérir deviennent <strong className="text-[#F05A1A]">ton capital à vie</strong>.
                  </p>
                  <p className="text-sm text-white/80 leading-relaxed mb-5 italic">
                    Tout ce que tu apprendras restera avec toi pour toujours — quelle que soit la suite de ton parcours, ton prochain projet, ta prochaine enseigne. Personne ne pourra te l'enlever.
                  </p>

                  {/* OBJECTIF FINAL — La vision de marque */}
                  <div className="rounded-xl p-4 mb-5" style={{ background: 'rgba(240,90,26,0.12)', border: '1px solid rgba(240,90,26,0.40)' }}>
                    <p className="text-[10px] font-black uppercase tracking-widest text-[#F05A1A] mb-2">🎯 Objectif final de l'accompagnement</p>
                    <p className="text-white font-bebas text-xl sm:text-2xl leading-tight mb-2">
                      Une marque <span className="text-[#F05A1A]">reconnue</span>, identifiable, <span className="text-[#F05A1A]">duplicable</span> et franchisable
                    </p>
                    <p className="text-[13px] text-white/85 leading-relaxed">
                      Construite sur un <strong className="text-white">modèle industriel</strong>, avec une <strong className="text-white">gestion des coûts et des marges pensée comme un industriel</strong>. C'est ça la cible. C'est ça ton intérêt. C'est ça qu'on bâtit ensemble.
                    </p>
                  </div>

                  {/* BOUSSOLE 80/10/10 — Répartition du temps dirigeant */}
                  <div className="rounded-xl p-4 mb-5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)' }}>
                    <p className="text-[10px] font-black uppercase tracking-widest text-white/70 mb-1">⏱ La règle 80 / 10 / 10 — Ta boussole de dirigeant</p>
                    <p className="text-[12px] text-white/75 italic mb-3">Comment tu dois répartir ton temps une fois qu'on aura mis les bases en place ensemble&nbsp;:</p>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="rounded-lg p-2.5 text-center" style={{ background: 'rgba(240,90,26,0.18)', border: '1px solid rgba(240,90,26,0.35)' }}>
                        <p className="font-bebas text-[#F05A1A] text-3xl leading-none mb-1">80%</p>
                        <p className="text-[10px] font-black uppercase tracking-wider text-white">Développement</p>
                        <p className="text-[10px] text-white/65 leading-tight mt-0.5">CA · marges · croissance</p>
                      </div>
                      <div className="rounded-lg p-2.5 text-center" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)' }}>
                        <p className="font-bebas text-white text-3xl leading-none mb-1">10%</p>
                        <p className="text-[10px] font-black uppercase tracking-wider text-white">Contrôle</p>
                        <p className="text-[10px] text-white/65 leading-tight mt-0.5">de ton activité</p>
                      </div>
                      <div className="rounded-lg p-2.5 text-center" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)' }}>
                        <p className="font-bebas text-white text-3xl leading-none mb-1">10%</p>
                        <p className="text-[10px] font-black uppercase tracking-wider text-white">Opérationnel</p>
                        <p className="text-[10px] text-white/65 leading-tight mt-0.5">le terrain quotidien</p>
                      </div>
                    </div>
                    <p className="text-[12px] text-white/75 mt-3 leading-relaxed">
                      <strong className="text-white">C'est sur ça qu'on bosse aussi avec toi.</strong> Aujourd'hui tu fais probablement l'inverse — tu passes 80% sur l'opérationnel et tu te demandes pourquoi ton CA stagne. On va inverser la pyramide.
                    </p>
                  </div>

                  <p className="text-[12px] font-black uppercase tracking-widest text-white/60 mb-3">Voici les compétences que tu vas développer&nbsp;:</p>

                  <ul className="space-y-2 mb-5">
                    {[
                      { ti: "Maîtriser des outils de productivité qui te rendent ton temps", de: "Tu vas apprendre à exploiter des outils qui font gagner des heures chaque semaine à un dirigeant — automatisation des tâches répétitives, organisation pro de ton travail, fini les post-its et les Whatsapp en boucle" },
                      { ti: "Devenir un dirigeant solide sur les fondamentaux", de: "Finance, RH, marketing, IA — tu décideras désormais en connaissance, plus jamais par feeling. Plus tu maîtrises, plus tu délègues, plus tu te concentres sur le développement" },
                      { ti: "Penser comme un industriel : coûts, marges, modèle duplicable", de: "Tu vas apprendre à voir ton resto comme une unité de production reproductible. Fini la cuisine à l'instinct — on calibre, on standardise, on pilote" },
                      { ti: "Construire une marque reconnue et identifiable", de: "ADN, histoire, émotion, code visuel — pour que demain ton concept soit duplicable dans une autre ville, dans un autre pays. Tu poses les bases d'un actif transmissible" },
                      { ti: "Garder une longueur d'avance sur les tendances 2026", de: "Une veille active sur ton secteur et les marchés voisins — réflexe de leader, vision claire des évolutions à venir" },
                      { ti: "Maîtriser ta présence sur les réseaux sociaux", de: "Instagram, TikTok, LinkedIn — en 2026, c'est une compétence-clé pour tout dirigeant qui veut peser sur son marché" },
                      { ti: "Savoir t'entourer de prestataires de qualité", de: "Photographe, vidéaste, graphiste — apprendre à reconnaître les pros, négocier le bon prix, et investir où ça compte vraiment" },
                      { ti: "Faire de l'IA un levier quotidien", de: "ChatGPT, Claude, génération d'images, automatisations — un atout différenciant qui te suivra dans toutes tes aventures d'entrepreneur" },
                      { ti: "Lire et piloter tes chiffres avec aisance", de: "Marges, food cost, masse salariale, point mort — la compréhension financière qui te libère de la dépendance à ton expert-comptable et te fait gagner des points de marge" },
                      { ti: "Adopter la posture 80/10/10 du vrai dirigeant", de: "Te libérer de l'opérationnel pour te concentrer sur le développement — c'est ce changement de posture qui transforme un restaurateur en chef d'entreprise" },
                    ].map((ex, i) => (
                      <li key={i} className="flex items-start gap-3 p-2.5 rounded-lg bg-white/5 border border-white/10">
                        <span className="inline-flex items-center justify-center flex-shrink-0 mt-0.5 w-6 h-6 rounded-full bg-[#F05A1A] text-white text-[11px] font-black">
                          {i + 1}
                        </span>
                        <span className="flex-1 text-[13px] leading-snug text-white/90">
                          <strong className="text-white block mb-0.5">{ex.ti}</strong>
                          <span className="text-[12px] text-white/65">{ex.de}</span>
                        </span>
                      </li>
                    ))}
                  </ul>

                  <p className="text-[13px] text-white/85 leading-relaxed mb-2">
                    Prends un instant pour te demander&nbsp;: <em className="text-white not-italic font-semibold">est-ce que je suis prêt à m'offrir cette transformation&nbsp;?</em>
                  </p>
                  <p className="text-[13px] text-white/75 leading-relaxed italic mb-4">
                    Si tu sens que ce n'est pas encore le bon moment, c'est OK. Prends le temps qu'il te faut. Cet engagement mérite ta pleine conviction — pas la précipitation. Reviens quand tu te sens vraiment prêt à investir sur toi.
                  </p>

                  {/* Inline checkbox for readiness */}
                  <label
                    className="flex items-start gap-3 p-4 rounded-xl cursor-pointer transition-all"
                    style={{
                      background: acceptedReadiness ? 'rgba(240,90,26,0.18)' : 'rgba(255,255,255,0.05)',
                      border: `2px solid ${acceptedReadiness ? '#F05A1A' : 'rgba(255,255,255,0.15)'}`,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={acceptedReadiness}
                      onChange={(e) => setAcceptedReadiness(e.target.checked)}
                      className="mt-0.5 w-5 h-5 accent-[#F05A1A] flex-shrink-0"
                      data-testid="offer-accept-readiness"
                    />
                    <span className="text-sm text-white leading-snug">
                      <strong className="text-[#F05A1A] block mb-1 text-[15px]">Oui, je suis prêt à investir sur moi</strong>
                      Je m'engage à <strong>développer ces compétences</strong> avec rigueur et enthousiasme. Je comprends que <strong>cet accompagnement transforme autant ma marque que moi-même</strong>, et que ce que j'apprendrai restera mon capital à vie. Je viens chercher l'excellence, et je suis prêt à me donner les moyens d'y parvenir.
                    </span>
                  </label>
                </div>

                {/* Acceptation */}
                <div className="bg-white rounded-2xl p-5 sm:p-6 mb-5 border border-[#e0dbd4]">
                  <p className="text-[11px] font-bold tracking-wider uppercase text-[#666] mb-4">8 · Acceptation</p>
                  <label className="flex items-start gap-3 mb-3 cursor-pointer">
                    <input type="checkbox" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} className="mt-1 w-4 h-4 accent-[#F05A1A] flex-shrink-0" data-testid="offer-accept-terms" />
                    <span className="text-sm text-[#1a1a1a]">J'ai lu et j'accepte le <strong>périmètre détaillé, les conditions tarifaires et l'engagement</strong>.</span>
                  </label>
                  <label className="flex items-start gap-3 mb-3 cursor-pointer">
                    <input type="checkbox" checked={acceptedNDA} onChange={(e) => setAcceptedNDA(e.target.checked)} className="mt-1 w-4 h-4 accent-[#F05A1A] flex-shrink-0" data-testid="offer-accept-nda" />
                    <span className="text-sm text-[#1a1a1a]">J'accepte la <strong>clause de confidentialité bilatérale</strong>.</span>
                  </label>
                  <label className="flex items-start gap-3 mb-3 cursor-pointer">
                    <input type="checkbox" checked={acceptedCommitment} onChange={(e) => setAcceptedCommitment(e.target.checked)} className="mt-1 w-4 h-4 accent-[#F05A1A] flex-shrink-0" data-testid="offer-accept-commitment" />
                    <span className="text-sm text-[#1a1a1a]">Je m'engage à <strong>m'impliquer activement</strong>, à <strong>faire les missions données entre les sessions</strong>, et je comprends que <strong>ma montée en compétence</strong> fait partie intégrante de la mission.</span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={acceptedWorkframe} onChange={(e) => setAcceptedWorkframe(e.target.checked)} className="mt-1 w-4 h-4 accent-[#F05A1A] flex-shrink-0" data-testid="offer-accept-workframe" />
                    <span className="text-sm text-[#1a1a1a]">J'accepte le <strong>cadre de communication</strong> : pas d'appels non planifiés, pas de messages audio, communication écrite, sessions planifiées par Sabril.</span>
                  </label>
                </div>

                {/* Signature */}
                <div className="bg-white rounded-2xl p-5 sm:p-6 mb-5 border border-[#e0dbd4]">
                  <p className="text-[11px] font-bold tracking-wider uppercase text-[#666] mb-4">9 · Signature électronique</p>
                  <SignaturePad onSignatureChange={setSignature} />
                  <p className="text-[10px] text-[#888] mt-2">
                    Fait à distance le {today}. Valeur légale (art. 1366 et 1367 du Code civil).
                  </p>
                </div>

                {/* Paiement */}
                <div className="bg-white rounded-2xl p-5 sm:p-6 mb-6 border border-[#e0dbd4]">
                  <p className="text-[11px] font-bold tracking-wider uppercase text-[#666] mb-4">10 · Mode de paiement</p>
                  <PaymentChoice value={paymentMethod} onChange={setPaymentMethod} hideStripe={!offer.price} />
                </div>
              </>
            )}

            {isDiscovery && (
              <div className="bg-white rounded-2xl p-5 sm:p-6 mb-6 border border-[#e0dbd4] flex items-start gap-3">
                <Phone className="w-5 h-5 text-[#F05A1A] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-[#1a1a1a] leading-relaxed">
                  <strong>Pas de paiement à cette étape.</strong> Sabril te contacte sous 24h pour un appel de qualification (45 min). Le devis et le contrat te sont envoyés après cet appel, ajustés à la taille de ton groupe.
                </p>
              </div>
            )}

            <motion.button
              whileHover={canSubmit ? { scale: 1.01 } : {}}
              whileTap={canSubmit ? { scale: 0.99 } : {}}
              type="button"
              disabled={!canSubmit || loading}
              onClick={handleSubmit}
              className="w-full py-5 rounded-2xl font-extrabold text-white text-base shadow-xl transition-all"
              style={{
                backgroundColor: canSubmit ? '#F05A1A' : '#bbb',
                opacity: canSubmit ? 1 : 0.6,
                cursor: canSubmit ? 'pointer' : 'not-allowed',
              }}
              data-testid="offer-submit-btn"
            >
              {loading
                ? 'Envoi en cours…'
                : isDiscovery
                  ? 'Envoyer ma demande'
                  : !allItemsConfirmed
                    ? `Valide les ${allScopeKeys.length - confirmedItems.size} point(s) restant(s)`
                    : paymentMethod === 'card'
                      ? `Signer & payer ${priceLabel}`
                      : paymentMethod === 'onsite'
                        ? `Signer & régler sur place`
                        : `Signer & recevoir le RIB`}
              <ArrowRight className="w-5 h-5 inline ml-2" />
            </motion.button>
            <p className="text-[10px] text-center mt-3 text-[#888]">
              🔒 {isDiscovery ? 'Aucun paiement n\'est demandé à cette étape' : 'Paiement Stripe sécurisé OU RIB transmis par email après signature'}
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default OfferContractModal;
